import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StrategyBuilder } from 'utils/shared/quantlib/StrategyBuilder';
import { StrategyPoint, CurveResult } from 'utils/shared/quantlib/strategyKernel';

const MS_PER_DAY = 86_400_000;

export interface TimeScrub {
  /** Selected evaluation timestamp (ms). Defaults to expiration until scrubbed. */
  evalMs: number;
  minMs: number;
  maxMs: number;
  /** True until the user moves the slider — default view stays byte-identical. */
  isDefault: boolean;
  isAtExpiration: boolean;
  /** Days remaining from the selected eval date to expiration (full term at "today", 0 at expiry). */
  daysToExpiration: number;
  /** rAF-coalesced setter: commits at most once per animation frame while dragging. */
  setEvalMs: (ms: number) => void;
  /** Drop back to the default (expiration) view. */
  reset: () => void;
  /** Payoff curve at evalMs on the same grid as the static graph. Empty until scrubbed. */
  liveCurve: StrategyPoint[];
  liveBreakEvens: number[];
  /** Value + greeks at current spot & evalMs. Null until scrubbed. */
  livePoint: StrategyPoint | null;
}

/**
 * Drives the draggable evaluation-time slider on the strategy page.
 *
 * The heavy lifting is already done: `results.kernel` is precomputed, so a full
 * payoff curve at any evaluation time is a ~sub-3ms `sweepCurve`. This hook just
 * coalesces drag events to one recompute per frame and exposes the live curve +
 * the value/greeks at the current spot for that time.
 *
 * Until the user touches the slider (`isDefault`), it returns no live data so
 * the page renders exactly as before (expiration curve, "now" greeks).
 */
export function useTimeScrub({
  results,
  spot,
  lowerBound,
  upperBound,
  steps,
  expirationMs,
  nowMs,
  defaultEvalMs,
}: {
  results: StrategyBuilder | undefined;
  spot: number;
  lowerBound: number;
  upperBound: number;
  steps: number;
  /** Right end of the slider — the LATEST leg expiry (so multi-expiry legs can
   * be scrubbed through, front leg settling while back legs stay live). */
  expirationMs: number;
  nowMs: number;
  /** Where the thumb rests before the user scrubs. Matches the default payoff
   * graph's evaluation date (the nearest/front expiry). Defaults to `expirationMs`
   * for single-expiry strategies, where nearest === latest. */
  defaultEvalMs?: number;
}): TimeScrub {
  const [evalMsState, setEvalMsState] = useState<number | null>(null);
  const isDefault = evalMsState === null;
  const evalMs = evalMsState ?? defaultEvalMs ?? expirationMs;

  const rafRef = useRef<number | null>(null);
  const pendingRef = useRef<number>(evalMs);
  const setEvalMs = useCallback((ms: number) => {
    pendingRef.current = ms;
    if (rafRef.current == null) {
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        setEvalMsState(pendingRef.current);
      });
    }
  }, []);

  const reset = useCallback(() => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    setEvalMsState(null);
  }, []);
  useEffect(
    () => () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    },
    [],
  );

  const curve: CurveResult | null = useMemo(() => {
    if (isDefault || !results) return null;
    // injectStrikes=true so the live curve keeps sharp corners at expiry and
    // stays index-aligned with the static payoffGraph (which also injects).
    return results.pricing.curve({
      from: lowerBound,
      to: upperBound,
      steps,
      asOf: evalMs,
      injectStrikes: true,
    });
  }, [isDefault, results, lowerBound, upperBound, steps, evalMs]);

  const livePoint = useMemo(() => {
    if (isDefault || !results) return null;
    return results.pricing.value({ spot, asOf: evalMs });
  }, [isDefault, results, spot, evalMs]);

  const daysToExpiration = Math.max(0, Math.round((expirationMs - evalMs) / MS_PER_DAY));

  return {
    evalMs,
    minMs: nowMs,
    maxMs: expirationMs,
    isDefault,
    isAtExpiration: evalMs >= expirationMs,
    daysToExpiration,
    setEvalMs,
    reset,
    liveCurve: curve?.payoff ?? [],
    liveBreakEvens: curve?.breakEvens ?? [],
    livePoint,
  };
}
